// Per-entity sample history and interpolation (protocol §8.3–8.4, movement.md §4.5).
//
// - Between samples: linear interpolation (unchanged).
// - Past the newest sample, only when the newest FRAME is late (`rt > timeline.newestTick`) and
//   the entity moved in that newest FRAME: extrapolate along its last per-tick velocity (newest −
//   previous sample) for at most `EXTRAPOLATE_MAX_TICKS` (1 tick), then hold. An entity that did
//   not move in the newest FRAME is held (it did not change), so a cell that stopped never
//   overshoots. The radius is never extrapolated.
// - When new data replaces an extrapolated (or still-blending) position, the drawn position
//   blends from where it was drawn to the true one over `ERROR_BLEND_MS` (100 ms of render time),
//   linearly, instead of snapping.
// - `timeline` is shared by every history and written by the tick clock (game/clock.ts) on each
//   FRAME; by default both use `FRAME_TIMELINE`. With an unknown newest tick (NaN, before the
//   first FRAME or with no clock), histories hold past the newest sample as before.
//
// Implementer: client net/game. Consumers: game/world-store.ts (one SampleHistory per entity),
// game/clock.ts (writes the timeline), the view-diff property test. DOM-free.

import { clamp01 } from '@ballio/shared';

/** One received state, in world units (dequantised). */
export interface Sample {
  tick: number;
  x: number;
  y: number;
  r: number;
}

/** Interpolation output. */
export interface Point3 {
  x: number;
  y: number;
  r: number;
}

/** Up to 4 samples per entity (INTERPOLATION.historySamples). */
export const HISTORY_SAMPLES = 4;

/** Longest extrapolation past the newest sample, in ticks (movement.md §4.5). */
export const EXTRAPOLATE_MAX_TICKS = 1;

/** A correction after extrapolating is blended in over this much render time (movement.md §4.5). */
export const ERROR_BLEND_MS = 100;

/** What every history needs to know about the FRAME stream (written by the tick clock). */
export interface FrameTimeline {
  /** Tick of the newest FRAME received; NaN = unknown (no extrapolation). */
  newestTick: number;
  /** ms per server tick (converts ERROR_BLEND_MS to ticks). */
  dtMs: number;
}

/** The timeline histories and the tick clock share by default (one game per page). */
export const FRAME_TIMELINE: FrameTimeline = { newestTick: Number.NaN, dtMs: 40 };

/** Errors below this (world units) are dropped instead of blended. */
const BLEND_EPS = 1e-6;

/**
 * A fixed-size ring of samples, oldest first, allocation-free after construction.
 * - `push(T, …)`: the hold-sample rule — when the newest sample is older than `T − 1`, first a
 *   copy of it stamped `T − 1` is pushed — then the new sample; the oldest falls off.
 * - `at(rt, out)`: linear interpolation between the two samples around render tick `rt`; before
 *   the oldest → the oldest; past the newest → bounded extrapolation or hold (see the header),
 *   plus the error blend.
 *
 * Ticks inside one history are strictly increasing: a second sample for the newest tick (or an
 * older one, which a TCP stream cannot produce) overwrites the newest values instead.
 */
export class SampleHistory {
  readonly capacity: number;
  readonly timeline: FrameTimeline;
  /** Preallocated samples; `ring[(head + i) % capacity]` is the i-th oldest. */
  private readonly ring: Sample[];
  private head = 0;
  private count = 0;

  /** Bumped on every data change (push, reset, clear). */
  private version = 0;
  /** `version` and `timeline.newestTick` as of the last `at` call. */
  private seenVersion = -1;
  private seenNewestTick = Number.NaN;
  /** The last `at` call: its render tick and output, and whether that output was adjusted. */
  private lastRt = Number.NaN;
  private lastX = 0;
  private lastY = 0;
  private lastR = 0;
  private lastAdjusted = false;
  private lastExtrapolated = false;
  /** The error being blended out: drawn − true at `blendFromRt`. */
  private blending = false;
  private errX = 0;
  private errY = 0;
  private errR = 0;
  private blendFromRt = 0;
  private readonly scratch: Point3 = { x: 0, y: 0, r: 0 };

  constructor(capacity: number = HISTORY_SAMPLES, timeline: FrameTimeline = FRAME_TIMELINE) {
    this.capacity = Math.max(1, Math.floor(capacity) || 1);
    this.timeline = timeline;
    this.ring = [];
    for (let i = 0; i < this.capacity; i++) this.ring.push({ tick: 0, x: 0, y: 0, r: 0 });
  }

  get length(): number {
    return this.count;
  }

  clear(): void {
    this.head = 0;
    this.count = 0;
    this.forgetDrawn();
  }

  /** Start a fresh history with one sample (ADD). */
  reset(tick: number, x: number, y: number, r: number): void {
    this.head = 0;
    this.count = 0;
    this.forgetDrawn();
    this.append(tick, x, y, r);
  }

  /** A MOVE at `tick` (hold-sample rule, then the sample). */
  push(tick: number, x: number, y: number, r: number): void {
    this.version++;
    if (this.count === 0) {
      this.append(tick, x, y, r);
      return;
    }
    const newest = this.slot(this.count - 1);
    if (tick <= newest.tick) {
      newest.x = x;
      newest.y = y;
      newest.r = r;
      return;
    }
    // Hold sample (protocol §8.3): without it, an entity that sat still and then moved would
    // appear to drift slowly from its old sample the whole time.
    if (newest.tick < tick - 1) this.append(tick - 1, newest.x, newest.y, newest.r);
    this.append(tick, x, y, r);
  }

  /** The newest sample (= the last values the server sent: MOVE deltas apply to it), or null. */
  newest(): Readonly<Sample> | null {
    return this.count > 0 ? this.slot(this.count - 1) : null;
  }

  /** The oldest kept sample, or null. */
  oldest(): Readonly<Sample> | null {
    return this.count > 0 ? this.slot(0) : null;
  }

  /** The i-th oldest sample (0 = oldest), or null when out of range. */
  get(i: number): Readonly<Sample> | null {
    return i >= 0 && i < this.count ? this.slot(i) : null;
  }

  /**
   * The drawn state at `rt` into `out`; false (and `out` untouched) when empty. Calling it again
   * with the same `rt` and no new data gives the same result.
   */
  at(rt: number, out: Point3): boolean {
    if (this.count === 0) return false;
    const newestTick = this.timeline.newestTick;
    const changed = this.version !== this.seenVersion || !sameTick(newestTick, this.seenNewestTick);
    if (changed && this.lastAdjusted && Number.isFinite(this.lastRt)) {
      // New data replaced what was drawn last: blend from the drawn position to the new truth.
      const t = this.scratch;
      this.raw(this.lastRt, t);
      const ex = this.lastX - t.x;
      const ey = this.lastY - t.y;
      const er = this.lastR - t.r;
      const w = this.blending ? this.blendWeight(this.lastRt) : 0;
      if (!this.lastExtrapolated && w > 0) {
        // Only a blend was running (no new prediction): keep its schedule, so FRAMEs arriving
        // during it do not stretch it, and fold whatever changed into the remaining error.
        this.errX = ex / w;
        this.errY = ey / w;
        this.errR = er / w;
      } else {
        this.errX = ex;
        this.errY = ey;
        this.errR = er;
        this.blendFromRt = this.lastRt;
      }
      this.blending =
        Math.abs(ex) > BLEND_EPS || Math.abs(ey) > BLEND_EPS || Math.abs(er) > BLEND_EPS;
    }
    this.seenVersion = this.version;
    this.seenNewestTick = newestTick;

    const extrapolated = this.raw(rt, out);
    let adjusted = extrapolated;
    if (this.blending) {
      const w = this.blendWeight(rt);
      if (w > 0) {
        out.x += this.errX * w;
        out.y += this.errY * w;
        out.r += this.errR * w;
        adjusted = true;
      } else {
        this.blending = false;
      }
    }
    if (Number.isFinite(rt)) {
      this.lastRt = rt;
      this.lastX = out.x;
      this.lastY = out.y;
      this.lastR = out.r;
      this.lastAdjusted = adjusted;
      this.lastExtrapolated = extrapolated;
    }
    return true;
  }

  /** The share of the blended error still drawn at `rt`: 1 → 0 linearly over ERROR_BLEND_MS. */
  private blendWeight(rt: number): number {
    const dtMs = this.timeline.dtMs > 0 ? this.timeline.dtMs : 40;
    const elapsed = rt - this.blendFromRt;
    if (!(elapsed > 0)) return 1;
    const w = 1 - (elapsed * dtMs) / ERROR_BLEND_MS;
    return w > 0 ? w : 0;
  }

  // -------------------------------------------------------------------------------------------

  /**
   * Interpolation (or bounded extrapolation) from the samples alone, into `out`. True when the
   * result was extrapolated (the next FRAME may correct it).
   */
  private raw(rt: number, out: Point3): boolean {
    const n = this.count;
    let a = this.slot(0);
    if (n === 1 || !(rt > a.tick)) {
      // Before (or at) the oldest sample, a single sample, or a NaN render tick.
      out.x = a.x;
      out.y = a.y;
      out.r = a.r;
      return false;
    }
    for (let i = 1; i < n; i++) {
      const b = this.slot(i);
      if (rt < b.tick) {
        const t = (rt - a.tick) / (b.tick - a.tick);
        out.x = a.x + (b.x - a.x) * t;
        out.y = a.y + (b.y - a.y) * t;
        out.r = a.r + (b.r - a.r) * t;
        return false;
      }
      a = b;
    }
    // Past the newest sample `a`.
    out.x = a.x;
    out.y = a.y;
    out.r = a.r;
    const newestTick = this.timeline.newestTick;
    // Extrapolate only while the FRAME after `a` is overdue and `a` came in the newest FRAME.
    if (!(rt > newestTick) || a.tick !== newestTick) return false;
    const p = this.slot(n - 2);
    if (p.tick !== a.tick - 1) return false;
    const k = Math.min(rt - a.tick, EXTRAPOLATE_MAX_TICKS);
    if (!(k > 0)) return false;
    out.x = a.x + (a.x - p.x) * k;
    out.y = a.y + (a.y - p.y) * k;
    return true;
  }

  private forgetDrawn(): void {
    this.version++;
    this.lastRt = Number.NaN;
    this.lastAdjusted = false;
    this.lastExtrapolated = false;
    this.blending = false;
  }

  private slot(i: number): Sample {
    return this.ring[(this.head + i) % this.capacity] as Sample;
  }

  private append(tick: number, x: number, y: number, r: number): void {
    let s: Sample;
    if (this.count < this.capacity) {
      s = this.slot(this.count);
      this.count++;
    } else {
      s = this.ring[this.head] as Sample;
      this.head = (this.head + 1) % this.capacity;
    }
    s.tick = tick;
    s.x = x;
    s.y = y;
    s.r = r;
  }
}

/** Tick equality where NaN equals NaN. */
function sameTick(a: number, b: number): boolean {
  return a === b || (Number.isNaN(a) && Number.isNaN(b));
}

/** Effect progress 0–1 for an effect that started at `startTick` and lasts `effectMs`. */
function progress(rt: number, startTick: number, dtMs: number, effectMs: number): number {
  if (!(effectMs > 0)) return rt >= startTick ? 1 : 0;
  return clamp01(((rt - startTick) * dtMs) / effectMs);
}

/** Eaten effect progress 0–1: `(rt − removedTick) · dtMs / effectMs`, clamped (protocol §8.4: 100 ms). */
export function eatenProgress(
  rt: number,
  removedTick: number,
  dtMs: number,
  effectMs = 100,
): number {
  return progress(rt, removedTick, dtMs, effectMs);
}

/** Food pop-in progress 0–1 from its ADD tick (protocol §8.4: 150 ms). */
export function popInProgress(rt: number, addedTick: number, dtMs: number, effectMs = 150): number {
  return progress(rt, addedTick, dtMs, effectMs);
}
